import React, { useState, useEffect, useCallback, useMemo } from 'react';

interface MeshNode {
  id: string;
  name: string;
  status: string;
  ingressUrl: string | null;
  capabilities: string[] | null;
  hardware: {
    os?: string;
    arch?: string;
    backend?: string;
    has_gpu?: boolean;
    device_name?: string;
  } | null;
  lastSeen: string | null;
}

export const ComfyMeshCoordinatorSection: React.FC = () => {
  const [nodes, setNodes] = useState<MeshNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(true);
  const [selectedCapability, setSelectedCapability] = useState('upscale');
  const [selectedBackend, setSelectedBackend] = useState<'cuda' | 'mps' | 'cpu'>('cuda');
  const [dispatchLog, setDispatchLog] = useState<{ status: 'success' | 'error'; message: string } | null>(null);
  const [isDispatching, setIsDispatching] = useState(false);

  const fetchNodes = useCallback(async () => {
    try {
      const res = await fetch('/api/nodes', { cache: 'no-store' });
      const data = await res.json();
      if (data.success && Array.isArray(data.nodes)) {
        setNodes(data.nodes);
      }
    } catch (err) {
      console.warn('[ComfyMesh] Failed to poll nodes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNodes();
    if (!isPolling) return;
    const interval = setInterval(fetchNodes, 3000);
    return () => clearInterval(interval);
  }, [fetchNodes, isPolling]);

  const activeNodes = useMemo(() => {
    return nodes.filter((n) => {
      const diff = (Date.now() - new Date(n.lastSeen || 0).getTime()) / 1000;
      return diff <= 30 && (n.status === 'ready' || n.status === 'online');
    });
  }, [nodes]);

  const handleTestDispatch = async () => {
    setIsDispatching(true);
    setDispatchLog(null);
    try {
      const res = await fetch('/api/mesh/dispatch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          capability: selectedCapability,
          preferredBackend: selectedBackend,
          prompt: { '3': { class_type: 'KSampler', inputs: { seed: 42, steps: 20 } } },
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setDispatchLog({
          status: 'success',
          message: `✓ Routed to ${data.node?.name} (${data.node?.ingressUrl})`,
        });
      } else {
        setDispatchLog({
          status: 'error',
          message: data.error || data.details || 'Dispatch rejected by coordinator',
        });
      }
    } catch (err: any) {
      setDispatchLog({ status: 'error', message: err.message || 'Dispatch network error' });
    } finally {
      setIsDispatching(false);
      fetchNodes();
    }
  };

  return (
    <div
      style={{
        background: 'rgba(15, 23, 42, 0.6)',
        border: '1px solid rgba(56, 189, 248, 0.25)',
        borderRadius: '10px',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginTop: '8px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>🎨 Distributed ComfyUI Workflow Mesh</span>
            <span style={{ fontSize: '10px', background: 'rgba(56, 189, 248, 0.2)', padding: '2px 6px', borderRadius: '10px', fontFamily: 'monospace' }}>
              {activeNodes.length} Online / {nodes.length} Registered
            </span>
          </div>
          <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
            Heterogeneous accelerator dispatching over Tailscale WireGuard (CUDA &amp; MPS Apple Silicon).
          </div>
        </div>

        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            type="button"
            onClick={() => setIsPolling((p) => !p)}
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              border: `1px solid ${isPolling ? 'rgba(52, 211, 153, 0.4)' : 'rgba(255, 255, 255, 0.12)'}`,
              background: isPolling ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.04)',
              color: isPolling ? '#34d399' : '#94a3b8',
              fontSize: '10px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {isPolling ? '● Live' : '○ Paused'}
          </button>
          <button
            type="button"
            onClick={fetchNodes}
            style={{
              padding: '4px 8px',
              borderRadius: '6px',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              background: 'rgba(255, 255, 255, 0.04)',
              color: '#f8fafc',
              fontSize: '10px',
              cursor: 'pointer',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Diagnostics Dispatcher Bench */}
      <div
        style={{
          background: 'rgba(0, 0, 0, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '8px',
          padding: '10px 12px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        <div style={{ fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
          Automated Task Dispatch Diagnostics
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            value={selectedCapability}
            onChange={(e) => setSelectedCapability(e.target.value)}
            style={{
              background: '#090d16',
              border: '1px solid #1e293b',
              borderRadius: '6px',
              padding: '5px 8px',
              color: '#f8fafc',
              fontSize: '11px',
              outline: 'none',
            }}
          >
            <option value="upscale">capability: upscale</option>
            <option value="remove-bg">capability: remove-bg</option>
            <option value="txt2img">capability: txt2img</option>
          </select>

          <select
            value={selectedBackend}
            onChange={(e) => setSelectedBackend(e.target.value as any)}
            style={{
              background: '#090d16',
              border: '1px solid #1e293b',
              borderRadius: '6px',
              padding: '5px 8px',
              color: '#f8fafc',
              fontSize: '11px',
              outline: 'none',
            }}
          >
            <option value="cuda">prefer: cuda (NVIDIA RTX)</option>
            <option value="mps">prefer: mps (Apple Silicon)</option>
            <option value="cpu">prefer: cpu</option>
          </select>

          <button
            type="button"
            onClick={handleTestDispatch}
            disabled={isDispatching}
            style={{
              padding: '5px 12px',
              borderRadius: '6px',
              border: 'none',
              background: '#0284c7',
              color: '#ffffff',
              fontSize: '11px',
              fontWeight: 700,
              cursor: isDispatching ? 'not-allowed' : 'pointer',
              opacity: isDispatching ? 0.6 : 1,
            }}
          >
            {isDispatching ? 'Routing...' : '⚡ Test Dispatch'}
          </button>
        </div>

        {dispatchLog && (
          <div
            style={{
              fontSize: '11px',
              fontFamily: 'monospace',
              padding: '6px 10px',
              borderRadius: '6px',
              background: dispatchLog.status === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
              border: `1px solid ${dispatchLog.status === 'success' ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
              color: dispatchLog.status === 'success' ? '#34d399' : '#fca5a5',
            }}
          >
            {dispatchLog.message}
          </div>
        )}
      </div>

      {/* Node Cards List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {loading ? (
          <div style={{ fontSize: '11px', color: '#64748b', padding: '8px 0' }}>Syncing node telemetry...</div>
        ) : nodes.length === 0 ? (
          <div style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic', padding: '8px 0' }}>
            No ComfyUI nodes currently registered. Run MeshNodeAgent on a worker to join the mesh.
          </div>
        ) : (
          nodes.map((node) => {
            const diff = (Date.now() - new Date(node.lastSeen || 0).getTime()) / 1000;
            const isOnline = diff <= 30 && (node.status === 'ready' || node.status === 'online');
            const backend = node.hardware?.backend?.toLowerCase() || 'cpu';

            return (
              <div
                key={node.id}
                style={{
                  background: '#090d16',
                  border: isOnline ? '1px solid #1e293b' : '1px solid rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  opacity: isOnline ? 1 : 0.6,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#f8fafc' }}>{node.name}</span>
                    <span
                      style={{
                        fontSize: '9px',
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: '4px',
                        background:
                          backend === 'cuda'
                            ? 'rgba(34, 197, 94, 0.15)'
                            : backend === 'mps'
                              ? 'rgba(168, 85, 247, 0.15)'
                              : 'rgba(255, 255, 255, 0.08)',
                        color:
                          backend === 'cuda'
                            ? '#4ade80'
                            : backend === 'mps'
                              ? '#c084fc'
                              : '#94a3b8',
                        border: `1px solid ${
                          backend === 'cuda'
                            ? 'rgba(34, 197, 94, 0.3)'
                            : backend === 'mps'
                              ? 'rgba(168, 85, 247, 0.3)'
                              : 'rgba(255, 255, 255, 0.12)'
                        }`,
                      }}
                    >
                      {backend.toUpperCase()} • {node.hardware?.device_name || 'CPU'}
                    </span>
                    {node.hardware?.os && (
                      <span style={{ fontSize: '9px', color: '#64748b', fontFamily: 'monospace' }}>
                        {node.hardware.os} ({node.hardware.arch})
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                    <span style={{ fontSize: '10px', fontFamily: 'monospace', color: '#60a5fa' }}>
                      {node.ingressUrl || 'No ingress advertised'}
                    </span>
                    {node.capabilities && node.capabilities.length > 0 && (
                      <span style={{ fontSize: '10px', color: '#64748b' }}>
                        [{node.capabilities.join(', ')}]
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ flexShrink: 0 }}>
                  <span
                    style={{
                      fontSize: '9px',
                      fontWeight: 700,
                      background: isOnline ? 'rgba(16, 185, 129, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                      color: isOnline ? '#34d399' : '#64748b',
                      border: `1px solid ${isOnline ? 'rgba(16, 185, 129, 0.3)' : 'rgba(255, 255, 255, 0.1)'}`,
                      padding: '2px 8px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <span
                      style={{
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        backgroundColor: isOnline ? '#10b981' : '#64748b',
                        display: 'inline-block',
                      }}
                    />
                    {isOnline ? 'ONLINE' : 'OFFLINE'}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
